// API client del módulo pipeline-constructor.
// Backend: /api/v1/admin/pipeline-constructor/* (Bearer JWT).

import { ensureFreshToken } from '@/lib/tokenRefresh';

import type {
  CoverageZoneDTO,
  InventoryItemDTO,
  LeadDTO,
  LeadDetailDTO,
  LeadFilters,
  RunDTO,
  TriggerRunResult,
} from './types';

const DJANGO_PROXY = '/django-api';
const BASE = '/api/v1/admin/pipeline-constructor';

async function getJwt(): Promise<string> {
  await ensureFreshToken();
  const t = localStorage.getItem('auth_token');
  if (!t) throw new Error('Sesión expirada. Inicia sesión nuevamente.');
  return t;
}

export class PipelineConstructorApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'PipelineConstructorApiError';
    this.status = status;
  }
}

async function djangoFetch<T = any>(path: string, init?: RequestInit): Promise<T> {
  const jwt = await getJwt();
  const headers: Record<string, string> = { Authorization: `Bearer ${jwt}` };
  if (init?.body !== undefined && !(init?.headers && (init.headers as Record<string, string>)['Content-Type'])) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    ...init,
    headers: { ...headers, ...(init?.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = err.detail || `Django ${res.status}: ${res.statusText}`;
    throw new PipelineConstructorApiError(
      typeof detail === 'string' ? detail : JSON.stringify(detail),
      res.status,
    );
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}


export const pipelineConstructorApi = {
  listLeads(
    filters: LeadFilters = {},
    pagination: { limit?: number; offset?: number } = {},
  ): Promise<LeadDTO[]> {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.project_type) params.set('project_type', filters.project_type);
    if (filters.state) params.set('state', filters.state);
    if (filters.min_score !== undefined) params.set('min_score', String(filters.min_score));
    params.set('limit', String(pagination.limit ?? 50));
    if (pagination.offset) params.set('offset', String(pagination.offset));
    return djangoFetch<LeadDTO[]>(`${BASE}/leads?${params.toString()}`);
  },

  getLead(leadId: number): Promise<LeadDetailDTO> {
    return djangoFetch<LeadDetailDTO>(`${BASE}/leads/${leadId}`);
  },

  dismissLead(leadId: number, reason: string = ''): Promise<{ status: string; lead_id: number; new_status: string }> {
    return djangoFetch(`${BASE}/leads/${leadId}/dismiss`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  listInventory(includeInactive = false): Promise<InventoryItemDTO[]> {
    const q = includeInactive ? '?include_inactive=true' : '';
    return djangoFetch<InventoryItemDTO[]>(`${BASE}/inventory${q}`);
  },

  listCoverageZones(): Promise<CoverageZoneDTO[]> {
    return djangoFetch<CoverageZoneDTO[]>(`${BASE}/coverage-zones`);
  },

  listRuns(limit = 10): Promise<RunDTO[]> {
    return djangoFetch<RunDTO[]>(`${BASE}/runs?limit=${limit}`);
  },

  triggerRun(sinceDays = 30): Promise<TriggerRunResult> {
    return djangoFetch<TriggerRunResult>(`${BASE}/runs`, {
      method: 'POST',
      body: JSON.stringify({ since_days: sinceDays }),
    });
  },
};
